<template>
    <div>
        <sonicu-header>Alarm Tracking</sonicu-header>
        <div :id="chartId" class="h-64"></div>
    </div>
</template>

<script>
import {iconNamesMixin} from "@/mixins/IconNamesMixin";
import {amchartsMixin} from "@/mixins/amchartsMixin";

import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";

export default {
    name: "RegionAlarmTrackingChart",
    mixins: [iconNamesMixin, amchartsMixin],
    props: {
        // Unique DOM id for the amCharts root (amCharts requires a distinct element id per chart).
        chartId: {
            type: String,
            required: true,
        },
        // One entry per day, each carrying a count per alarm type, e.g.
        // { label: "Jul 1", thresholdAlarm, thresholdSnoozed, offlineAlarm, offlineSnoozed }
        data: {
            type: Array,
            default: () => [],
        },
    },
    created() {
        // Non-reactive: this holds a live am5 Root. Vue's reactive() proxies
        // deeply, and am5 detaches a child's canvas display by === identity, so
        // an am5 object that exists as both a raw object and a proxy never gets
        // removed from its parent's display list and keeps painting as a ghost.
        // See the note in Am5Series' constructor.
        this.root = null;
    },
    mounted() {
        this.initializeAM5Charts();
        this.makeChart();
    },
    beforeUnmount() {
        if (this.root) {
            this.root.dispose();
        }
    },
    methods: {
        makeChart() {
            let root = am5.Root.new(this.chartId);

            let chart = root.container.children.push(
                am5xy.XYChart.new(root, {
                    panX: false,
                    panY: false,
                    layout: root.verticalLayout,
                })
            );

            let xAxis = chart.xAxes.push(
                am5xy.CategoryAxis.new(root, {
                    categoryField: "label",
                    renderer: am5xy.AxisRendererX.new(root, {
                        cellStartLocation: 0.15,
                        cellEndLocation: 0.85,
                    }),
                })
            );
            xAxis.data.setAll(this.data);

            let yAxis = chart.yAxes.push(
                am5xy.ValueAxis.new(root, {
                    min: 0,
                    renderer: am5xy.AxisRendererY.new(root, {}),
                })
            );

            let legend = chart.children.push(
                am5.Legend.new(root, {
                    centerX: am5.percent(50),
                    x: am5.percent(50),
                })
            );

            // Each series is one alarm type; stacked: true stacks them into a single
            // per-day bar so the bar height is the day's total alarm count.
            let makeSeries = (name, field, color) => {
                let series = chart.series.push(
                    am5xy.ColumnSeries.new(root, {
                        name: name,
                        stacked: true,
                        xAxis: xAxis,
                        yAxis: yAxis,
                        valueYField: field,
                        categoryXField: "label",
                        fill: am5.color(color),
                        stroke: am5.color(color),
                    })
                );
                series.columns.template.setAll({
                    tooltipText: "{name}: {valueY}",
                    width: am5.percent(100),
                    strokeOpacity: 0,
                });
                series.data.setAll(this.data);
                legend.data.push(series);
            };

            // Colors grouped by family so each alarm type has a full + snoozed shade:
            // threshold = red + caution yellow, offline = grey + dark slate. The active
            // (full) shades mirror the pie / DashboardPointStatusSummary palette.
            makeSeries("Threshold Alarm", "thresholdAlarm", "#c53030");
            makeSeries("Threshold Snoozed", "thresholdSnoozed", "#f6e05e");
            makeSeries("Offline Alarm", "offlineAlarm", "#a0aec0");
            makeSeries("Offline Snoozed", "offlineSnoozed", "#4a5568");

            this.root = root;
        },
    },
};
</script>

<style scoped>
</style>
